import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, tap } from "rxjs/operators";

import { SubtaskModel } from "../../service/task-mgmt.service";
import { TaskpanelService, TagItem } from "../../service/taskpanel.service";
import moment from "moment";

interface Option {
  value: string;
  viewValue: string;
}

interface AdvancedSearchByOption {
  value: "name" | "id";
  viewValue: string;
}

interface AdvancedColumnConfig {
  field: "id" | "name";
  header: string;
  width?: string;
}

@Component({
    selector: "app-subtask-form",
    templateUrl: "./subtask-form.component.html",
    styleUrls: ["./subtask-form.component.scss"],
    standalone: false
})
export class SubtaskFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() subtask!: SubtaskModel;
  @Input() index = 0;
  @Input() canEdit = true;

  @Output() subtaskChange = new EventEmitter<SubtaskModel>();
  @Output() removeSubtask = new EventEmitter<number>();

  @ViewChild('subtaskAttachInput') attachInput!: ElementRef<HTMLInputElement>;

  reqSubcription: Subscription[] = [];
  private tagLoadSub: Subscription | null = null;

  taskTypeOptions: Option[] = [
    { value: "patient-followup", viewValue: "Patient FollowUp" },
    { value: "prescriber-followup", viewValue: "Prescriber FollowUp" },
    { value: "account-followup", viewValue: "Account FollowUp" },
  ];

  statusOptions: Option[] = [
    { value: "planned", viewValue: "Planned" },
    { value: "in-progress", viewValue: "In Progress" },
    { value: "cancelled", viewValue: "Cancelled" },
    { value: "done", viewValue: "Done" },
  ];

  priorityLevelOptions: Option[] = [
    { value: "low", viewValue: "Low" },
    { value: "medium", viewValue: "Medium" },
    { value: "high", viewValue: "High" },
  ];

  assignToOptions: Option[] = [
    { value: "robert", viewValue: "Robert Boe" },
    { value: "user1", viewValue: "User 1" },
    { value: "user2", viewValue: "User 2" },
  ];

  tagTypeOptions: Option[] = [
    { value: "patient-id", viewValue: "Patient ID" },
    { value: "prescriber-id", viewValue: "Prescriber ID" },
    { value: "account-id", viewValue: "Account ID" },
  ];

  // ========== Tag Typeahead ==========
  tagItems: TagItem[] = [];
  tagPage = 1;
  tagPageSize = 50;
  tagTotal = 0;
  tagLoading = false;
  tagHasMore = true;
  tagSearch$ = new Subject<string>();
  currentTagSearch = '';

  // ========== Advanced Search ==========
  advancedSearchOpen = false;
  advancedSearchResults: TagItem[] = [];
  advancedSearchLoading = false;
  advancedSearchForm!: FormGroup;
  advancedSearchPage = 1;
  advancedSearchPageSize = 100;
  advancedSearchHasMore = true;
  advancedSearchPerformed = false;
  advancedResultsVisible = true;
  selectedTagMap = new Map<number, TagItem>();
  advancedSearchBy: "name" | "id" = "name";

  advancedSearchByConfigs: { [tagType: string]: AdvancedSearchByOption[] } = {
    "patient-id": [
      { value: "name", viewValue: "Patient Name" },
      { value: "id", viewValue: "Patient ID" },
    ],
    "prescriber-id": [
      { value: "name", viewValue: "Prescriber Name" },
      { value: "id", viewValue: "Prescriber ID" },
    ],
    "account-id": [
      { value: "name", viewValue: "Account Name" },
      { value: "id", viewValue: "Account ID" },
    ],
  };

  advancedColumnConfigs: { [tagType: string]: AdvancedColumnConfig[] } = {
    "patient-id": [
      { field: "id", header: "Patient ID", width: "120px" },
      { field: "name", header: "Patient Name" },
    ],
    "prescriber-id": [
      { field: "id", header: "Prescriber ID", width: "120px" },
      { field: "name", header: "Prescriber Name" },
    ],
    "account-id": [
      { field: "id", header: "Account ID", width: "120px" },
      { field: "name", header: "Account Name" },
    ],
  };

  private previousTagType = '';

  get currentSearchByOptions(): AdvancedSearchByOption[] {
    return this.advancedSearchByConfigs[this.subtask.tagType] || [];
  }

  get currentColumns(): AdvancedColumnConfig[] {
    return this.advancedColumnConfigs[this.subtask.tagType] || [];
  }

  get selectedTagList(): TagItem[] {
    return Array.from(this.selectedTagMap.values());
  }

  constructor(
    private fb: FormBuilder,
    private tagService: TaskpanelService,
    private el: ElementRef
  ) {}

  ngOnInit(): void {
    this.previousTagType = this.subtask.tagType;

    // Advanced search form
    this.advancedSearchForm = this.fb.group({
      searchText: [""],
    });

    // Auto-search on typing (>4 chars)
    this.reqSubcription.push(
      this.advancedSearchForm.get("searchText")!.valueChanges
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe((val: string) => {
          if (val && val.length > 4) {
            this.onAdvancedSearch();
          } else if (!val || val.length === 0) {
            this.advancedSearchResults = [];
            this.advancedSearchPerformed = false;
          }
        })
    );

    // Tag typeahead debounce
    this.reqSubcription.push(
      this.tagSearch$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap((term) => {
            this.currentTagSearch = term;
            this.resetTagsPage();
            this.loadTagsPage(1, term);
          })
        )
        .subscribe()
    );

    // Initial tag load if tagType is preset
    if (this.subtask.tagType) {
      this.resetTagsPage();
      this.loadTagsPage(1);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subtask'] && !changes['subtask'].firstChange) {
      const newTagType = this.subtask.tagType;
      if (newTagType !== this.previousTagType) {
        this.previousTagType = newTagType;
        this.advancedSearchOpen = false;
        this.selectedTagMap.clear();
        if (newTagType) {
          this.resetTagsPage();
          this.loadTagsPage(1);
        } else {
          this.tagItems = [];
          this.tagTotal = 0;
          this.tagHasMore = false;
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.tagLoadSub?.unsubscribe();
    this.reqSubcription.forEach(s => s.unsubscribe());
  }

  getStatusLabel(value: string): string {
    return this.statusOptions.find(s => s.value === value)?.viewValue || '';
  }

  getPriorityLabel(value: string): string {
    return this.priorityLevelOptions.find(p => p.value === value)?.viewValue || '';
  }

  onFieldChange(field: keyof SubtaskModel, value: any): void {
    const updated = { ...this.subtask, [field]: value };
    this.subtaskChange.emit(updated);
  }

  getRemindDateTimeValue(): Date | null {
    if (!this.subtask.remindDate) return null;
    const timeStr = this.subtask.remindTime || '00:00';
    const d = new Date(`${this.subtask.remindDate}T${timeStr}`);
    return isNaN(d.getTime()) ? null : d;
  }

  onRemindDateTimeChange(date: Date | null): void {
    if (!date) {
      this.subtaskChange.emit({ ...this.subtask, remindDate: null, remindTime: null });
      return;
    }
    const m = moment(date);
    this.subtaskChange.emit({
      ...this.subtask,
      remindDate: m.format('YYYY-MM-DD'),
      remindTime: m.format('HH:mm'),
    });
  }

  getRemindDateTimeStr(): string {
    if (!this.subtask.remindDate) return '';
    const d = this.subtask.remindDate;
    const t = this.subtask.remindTime || '00:00';
    return `${moment(d).format('YYYY-MM-DD')}T${t}`;
  }

  onRemindDateTimeInputChange(value: string): void {
    if (!value) {
      this.subtaskChange.emit({ ...this.subtask, remindDate: null, remindTime: null });
      return;
    }
    const [datePart, timePart] = value.split('T');
    this.subtaskChange.emit({ ...this.subtask, remindDate: datePart, remindTime: timePart || null });
  }

  onRemindMeChange(checked: boolean): void {
    const updated = { ...this.subtask, remindMe: checked };
    if (!checked) {
      updated.remindDate = null;
      updated.remindTime = null;
    }
    this.subtaskChange.emit(updated);
  }

  onRemove(): void {
    this.removeSubtask.emit(this.index);
  }

  // ========== TAG PAGINATION ==========
  resetTagsPage(): void {
    this.tagPage = 1;
    this.tagTotal = 0;
    this.tagHasMore = true;
    this.tagItems = [];
  }

  loadTagsPage(page: number, searchTerm: string = this.currentTagSearch): void {
    const tagType = this.subtask.tagType;
    if (!tagType) {
      this.tagItems = [];
      this.tagTotal = 0;
      this.tagHasMore = false;
      return;
    }

    this.tagLoading = true;
    this.tagLoadSub?.unsubscribe();
    this.tagLoadSub = this.tagService.getTags(tagType, page, this.tagPageSize, searchTerm).subscribe({
        next: (res) => {
          if (page === 1) {
            this.tagItems = res.items;
          } else {
            this.tagItems = [...this.tagItems, ...res.items];
          }
          this.tagTotal = res.total;
          this.tagPage = page;
          this.tagHasMore = this.tagItems.length < this.tagTotal;
          this.tagLoading = false;
        },
        error: () => {
          this.tagLoading = false;
        },
      });
  }

  onTagScrollEnd(): void {
    if (this.tagHasMore && !this.tagLoading) {
      this.loadTagsPage(this.tagPage + 1);
    }
  }

  // ========== ADVANCED SEARCH ==========
  toggleAdvancedSearch(): void {
    if (!this.subtask.tagType) return;

    this.advancedSearchOpen = !this.advancedSearchOpen;
    if (this.advancedSearchOpen) {
      this.advancedSearchResults = [];
      this.advancedSearchForm.reset();
      this.advancedSearchBy = "name";
      this.advancedSearchPerformed = false;
      this.advancedResultsVisible = true;
      this.advancedSearchPage = 1;
      this.advancedSearchHasMore = true;

      // Populate selectedTagMap from current subtask tags
      const selectedIds: number[] = this.subtask.tags || [];
      this.selectedTagMap.clear();
      for (const id of selectedIds) {
        const item = this.tagItems.find((t) => t.id === id);
        if (item) this.selectedTagMap.set(id, item);
      }
    }
  }

  onAdvancedSearch(): void {
    this.advancedSearchPage = 1;
    this.advancedSearchHasMore = true;
    this.advancedSearchResults = [];
    this.advancedSearchPerformed = true;
    this.advancedResultsVisible = true;
    this.loadAdvancedSearchPage(1);
  }

  loadAdvancedSearchPage(page: number): void {
    const tagType = this.subtask.tagType;
    if (!tagType) return;

    this.advancedSearchLoading = true;
    const searchText = this.advancedSearchForm.get("searchText")?.value || "";

    if (this.advancedSearchBy === "name") {
      this.reqSubcription.push(
        this.tagService.getTags(tagType, page, this.advancedSearchPageSize, searchText).subscribe({
          next: (res) => this.applyAdvancedResults(page, res.items, res.total),
          error: () => (this.advancedSearchLoading = false),
        })
      );
    } else {
      this.reqSubcription.push(
        this.tagService.getTags(tagType, page, this.advancedSearchPageSize, "").subscribe({
          next: (res) => {
            const filtered = searchText
              ? res.items.filter((t) => t.id.toString().includes(searchText))
              : res.items;
            this.applyAdvancedResults(page, filtered, res.total);
          },
          error: () => (this.advancedSearchLoading = false),
        })
      );
    }
  }

  private applyAdvancedResults(page: number, items: TagItem[], serverTotal: number): void {
    if (page === 1) {
      this.advancedSearchResults = items;
    } else {
      this.advancedSearchResults = [...this.advancedSearchResults, ...items];
    }
    this.advancedSearchPage = page;
    this.advancedSearchHasMore = page * this.advancedSearchPageSize < serverTotal;
    this.advancedSearchLoading = false;
  }

  onAdvancedResultsScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
      if (this.advancedSearchHasMore && !this.advancedSearchLoading) {
        this.loadAdvancedSearchPage(this.advancedSearchPage + 1);
      }
    }
  }

  isAdvancedItemSelected(item: TagItem): boolean {
    return this.selectedTagMap.has(item.id);
  }

  toggleAdvancedItem(item: TagItem): void {
    if (this.selectedTagMap.has(item.id)) {
      this.selectedTagMap.delete(item.id);
    } else {
      this.selectedTagMap.set(item.id, item);
    }
    this.syncSelectedTagsToSubtask();
  }

  removeSelectedTag(id: number): void {
    this.selectedTagMap.delete(id);
    this.syncSelectedTagsToSubtask();
  }

  private syncSelectedTagsToSubtask(): void {
    const ids = Array.from(this.selectedTagMap.keys());
    this.subtaskChange.emit({ ...this.subtask, tags: ids });
  }

  // ========== ATTACHMENTS ==========
  onAttachmentsSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const newFiles = Array.from(input.files);
    const updated = { ...this.subtask, attachments: [...(this.subtask.attachments || []), ...newFiles] };
    this.subtaskChange.emit(updated);
    input.value = '';
  }

  removeAttachment(index: number): void {
    const attachments = [...(this.subtask.attachments || [])];
    attachments.splice(index, 1);
    this.subtaskChange.emit({ ...this.subtask, attachments });
  }
}
